import { MAX_ITEMS_PER_ORDER, MAX_QTY_PER_ITEM } from "../../shared/order";
import type { Localized, Product, ProductVariant } from "../../shared/types";
import { isCondition, isFinish, type VariantKey } from "../../shared/variants";

/** Snapshot of product + variant data stored with each line so the cart renders without refetching. */
export interface CartLine {
  variantId: number;
  productId: number;
  slug: string;
  name: Localized;
  variant: VariantKey;
  price: number;
  /** Fixed peso price set by an admin (absent in older carts → derived from price). */
  priceArs?: number | null;
  imageKey: string;
  stock: number;
  quantity: number;
  /** Pre-sale product: the order ships when it arrives (absent in carts saved before pre-sales). */
  presale?: boolean;
}

export interface CartState {
  lines: CartLine[];
}

export type CartAction =
  | { type: "add"; product: Product; variant: ProductVariant; quantity?: number }
  | { type: "setQuantity"; variantId: number; quantity: number }
  | { type: "remove"; variantId: number }
  | { type: "clear" }
  | { type: "replace"; state: CartState };

export const EMPTY_CART: CartState = { lines: [] };

export function maxQuantityFor(stock: number): number {
  return Math.max(0, Math.min(stock, MAX_QTY_PER_ITEM));
}

function clampQuantity(quantity: number, stock: number): number {
  if (!Number.isFinite(quantity)) return 0;
  return Math.max(0, Math.min(Math.floor(quantity), maxQuantityFor(stock)));
}

export function cartReducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case "add": {
      const { product, variant } = action;
      const qty = action.quantity ?? 1;
      if (variant.stock <= 0 || qty <= 0) return state;
      const existing = state.lines.find((l) => l.variantId === variant.id);
      if (existing) {
        const quantity = clampQuantity(existing.quantity + qty, variant.stock);
        return {
          lines: state.lines.map((l) => (l.variantId === variant.id ? { ...l, quantity, stock: variant.stock, price: variant.price, priceArs: variant.priceArs ?? null } : l)),
        };
      }
      if (state.lines.length >= MAX_ITEMS_PER_ORDER) return state;
      const line: CartLine = {
        variantId: variant.id,
        productId: product.id,
        slug: product.slug,
        name: product.name,
        variant: { condition: variant.condition, finish: variant.finish, language: variant.language },
        price: variant.price,
        priceArs: variant.priceArs ?? null,
        imageKey: product.imageKey,
        stock: variant.stock,
        quantity: clampQuantity(qty, variant.stock),
        ...(product.presale ? { presale: true } : {}),
      };
      return { lines: [...state.lines, line] };
    }
    case "setQuantity": {
      const line = state.lines.find((l) => l.variantId === action.variantId);
      if (!line) return state;
      const quantity = clampQuantity(action.quantity, line.stock);
      if (quantity === 0) return { lines: state.lines.filter((l) => l.variantId !== action.variantId) };
      return { lines: state.lines.map((l) => (l.variantId === action.variantId ? { ...l, quantity } : l)) };
    }
    case "remove":
      return { lines: state.lines.filter((l) => l.variantId !== action.variantId) };
    case "clear":
      return EMPTY_CART;
    case "replace":
      return action.state;
    default:
      return state;
  }
}

export function cartCount(state: CartState): number {
  return state.lines.reduce((n, l) => n + l.quantity, 0);
}

export function cartSubtotal(state: CartState): number {
  return state.lines.reduce((sum, l) => sum + l.price * l.quantity, 0);
}

function isCartLine(value: unknown): value is CartLine {
  if (typeof value !== "object" || value === null) return false;
  const l = value as Record<string, unknown>;
  const name = l.name as Record<string, unknown> | undefined;
  const variant = l.variant as Record<string, unknown> | undefined;
  return (
    Number.isInteger(l.variantId) &&
    Number.isInteger(l.productId) &&
    typeof l.slug === "string" &&
    typeof name?.es === "string" &&
    typeof name?.en === "string" &&
    isCondition(variant?.condition) &&
    isFinish(variant?.finish) &&
    typeof variant?.language === "string" &&
    Number.isFinite(l.price) &&
    (l.priceArs == null || (Number.isInteger(l.priceArs) && (l.priceArs as number) > 0)) &&
    typeof l.imageKey === "string" &&
    Number.isInteger(l.stock) &&
    Number.isInteger(l.quantity) &&
    (l.quantity as number) > 0
  );
}

/** Restores a cart from untrusted storage, dropping anything malformed (including pre-variant carts). */
export function deserializeCart(raw: string | null): CartState {
  if (!raw) return EMPTY_CART;
  try {
    const parsed = JSON.parse(raw) as { lines?: unknown };
    if (!Array.isArray(parsed.lines)) return EMPTY_CART;
    const lines = parsed.lines
      .filter(isCartLine)
      .map((l) => ({ ...l, quantity: clampQuantity(l.quantity, l.stock) }))
      .filter((l) => l.quantity > 0)
      .slice(0, MAX_ITEMS_PER_ORDER);
    return { lines };
  } catch {
    return EMPTY_CART;
  }
}

/**
 * Joins the cart on this device with the one saved on the server (signing in on another device):
 * every product from both, the larger quantity when both have it, this device's details first.
 */
export function mergeCarts(local: CartState, remote: CartState): CartState {
  const byVariant = new Map(local.lines.map((l) => [l.variantId, l]));
  for (const r of remote.lines) {
    const l = byVariant.get(r.variantId);
    byVariant.set(r.variantId, l ? { ...l, quantity: clampQuantity(Math.max(l.quantity, r.quantity), l.stock) } : r);
  }
  return { lines: [...byVariant.values()].filter((l) => l.quantity > 0).slice(0, MAX_ITEMS_PER_ORDER) };
}
